/**
 * Пометка качества без шума: если у всех плиток с значением стоит «частично» и причина у них одна, причину пишем один
 * раз над плитками, а у самих плиток пометку убираем. Если причины разные (или плиток меньше двух) — пометки остаются
 * на плитках, как раньше. Чистые функции, без React.
 */

import { issueText, QUALITY_HINTS } from '../../metrics-explorer/labels'
import type { TileModel } from '../../metrics-explorer/resultModel'
import type { ExplorerResult } from '../../metrics-explorer/types'

/** Коды проблем качества ответа по перечисленным метрикам (без повторов, порядок появления). */
export function issueCodesOf(result: Pick<ExplorerResult, 'quality'> | null | undefined, metricIds: readonly string[]): string[] {
  const codes = new Set<string>()
  for (const issue of result?.quality?.issues ?? []) if (metricIds.includes(issue.metricId)) codes.add(issue.code)
  return [...codes]
}

/**
 * Общая причина «частично» для набора плиток: у каждой плитки — её коды проблем. Причина одна, если у всех плиток один и
 * тот же единственный код (текст — по словарю) либо у всех кодов нет (тогда — общее пояснение «частично»). Иначе и при
 * плитках меньше двух — `null`: пометки остаются на плитках.
 */
export function commonPartialReason(codesPerTile: ReadonlyArray<readonly string[]>): string | null {
  if (codesPerTile.length < 2) return null
  const keys = codesPerTile.map((codes) => [...new Set(codes)].sort().join('|'))
  if (keys.some((key) => key !== keys[0])) return null
  const codes = keys[0] === '' ? [] : keys[0].split('|')
  if (codes.length > 1) return null
  return codes.length === 1 ? issueText(codes[0]) : QUALITY_HINTS.partial
}

/**
 * Плитки блока без повторяющейся пометки. Плитки «нет данных» в расчёте не участвуют и свою пометку сохраняют;
 * `reason` — причина для пометки над плитками (`null` — пометки остаются на плитках).
 */
export function collapsePartialTiles(tiles: TileModel[], result: ExplorerResult | null): { tiles: TileModel[]; reason: string | null } {
  const shown = tiles.filter((tile) => tile.quality !== 'unavailable')
  if (shown.length < 2 || shown.some((tile) => tile.quality !== 'partial')) return { tiles, reason: null }
  const reason = commonPartialReason(shown.map((tile) => issueCodesOf(result, [tile.id])))
  if (!reason) return { tiles, reason: null }
  return { tiles: tiles.map((tile) => (tile.quality === 'partial' ? { ...tile, qualityLabel: null } : tile)), reason }
}
